import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import { Pagination } from "components"
import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"
import useStockAdjustmentCorrectionReportRepositories from "repositories/stock-adjustment-correction-report"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")
const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const DirectionBadge = ({ direction }) => {
  const label = direction === "IN" ? "MASUK" : direction === "OUT" ? "KELUAR" : "-"
  const className = direction === "IN"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-rose-50 text-rose-700 border-rose-200"

  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold border rounded ${className}`}>
      {label}
    </span>
  )
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || toInputDate(new Date(Date.now() - 86400000))
  const dateTo = searchParams.get("date_to") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || "ALL"
  const direction = searchParams.get("direction") || "ALL"
  const search = searchParams.get("search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"

  const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false })
  const branchOptions = useMemo(() => {
    const baseOptions = Array.isArray(branchSelection) ? branchSelection : []

    return [
      { id: "ALL", name: "Semua Outlet", description: "Gabungan semua outlet" },
      ...baseOptions,
    ]
  }, [branchSelection])

  const { data: reportData, isLoading } = useStockAdjustmentCorrectionReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: outletId,
    direction,
    search,
    page,
    per_page: perPage,
    enabled: true,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.rows ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items

  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum export")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      outlet_id: outletId,
      direction,
    })

    if (search) query.set("search", search)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/stock-adjustment-correction/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Koreksi Stock</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Koreksi masuk dan keluar dari stock adjustment posted.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end xl:justify-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Outlet</label>
            <SelectDescription
              selection={branchOptions}
              isLoading={isLoadingBranches}
              value={outletId}
              onChange={(value) => mergeSearchParams({ outlet_id: value || "ALL", page: "1" })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Select Outlet"
            />
          </div>

          <div className="min-w-[150px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Type</label>
            <select
              value={direction}
              onChange={(event) => mergeSearchParams({ direction: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              <option value="ALL">ALL</option>
              <option value="IN">MASUK</option>
              <option value="OUT">KELUAR</option>
            </select>
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Search</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={search}
              onChange={(event) => mergeSearchParams({ search: event.target.value, page: "1" })}
              placeholder="No bukti / barang"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Date From</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Date To</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Per Page</label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[10, 20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-neutral-900 hover:bg-neutral-800"
          >
            Export Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Baris</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_rows)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Koreksi Masuk</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_in_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Koreksi Keluar</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_out_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Net Qty</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.net_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Nilai Masuk</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_in_amount)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Nilai Keluar</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_out_amount)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"} | {reportData?.direction_label || "SEMUA KOREKSI"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">NO BUKTI</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">TANGGAL</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">OUTLET</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">SUMBER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">ARAH</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">SKU</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">BARANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">MASUK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">KELUAR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">HPP</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">Memuat data report...</td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">Tidak ada koreksi stock untuk periode yang dipilih.</td>
                </tr>
              )}

              {!isLoading && rows.map((row, index) => (
                <tr key={row.row_key || `${row.adjustment_no}-${row.product_unit_id}-${index}`} className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-medium whitespace-nowrap text-neutral-900">{row.adjustment_no || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{formatDate(row.adjustment_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.branch_name || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.source_label || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><DirectionBadge direction={row.direction} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.product_code || row.product_unit_id || "-"}</td>
                  <td className="px-4 py-3 text-neutral-800">{row.product_name || "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.in_qty)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.out_qty)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatCurrency(row.unit_cost)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(row.amount)}</td>
                </tr>
              ))}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900" colSpan={7}>GRAND TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_in_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_out_qty)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_amount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}
